import React from 'react';
import SearchableSelect from './SearchableSelect';
import { CATEGORIES } from '../constants/objects';

const ReadModePanel = ({
  category, setCategory,
  queryObj, onObjectChange,
  filteredObjects,
  selectedFields,
  onOpenFieldPicker,
  useLabelHeaders, setUseLabelHeaders,
  sqlFilter,
  onOpenFilterBuilder,
  isInspecting,
  // Props for legacy query mode (readByQuery)
  useLegacyQuery = false,
  setUseLegacyQuery,
  legacyQueryString = '',
  setLegacyQueryString,
  // Props for GET mode (read/readByName)
  useGetMode = false,
  setUseGetMode,
  getExcelRef = '',
  onLinkGetExcelRange,
  getKeyType = 'name',
  setGetKeyType,
  exportGetXml = false,
  setExportGetXml,
}) => {
  return (
    <div className="bg-gray-800 p-4 rounded shadow-sm border border-gray-700 mb-6">
      <div className="mb-3">
        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Area</label>
        <select 
          value={category} 
          onChange={(e) => setCategory(e.target.value)} 
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-white"
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      
      <div className="mb-4">
        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Object</label>
        <SearchableSelect 
          options={filteredObjects.map(o => ({ id: o.id, label: o.name }))} 
          value={queryObj} 
          onChange={onObjectChange} 
          placeholder="Select Object..." 
        />
        {isInspecting && <div className="text-[10px] text-yellow-400 mt-1 animate-pulse">⚡ Scanning schema...</div>}
      </div>
      
      {/* Columns section - Hidden in GET mode since it returns all fields */}
      {!useGetMode && (
        <div className="mb-4">
          <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Columns</label>
          <div className="border border-gray-600 rounded p-2 bg-gray-700 flex flex-wrap gap-1 min-h-[42px] relative">
            {selectedFields.slice(0, 5).map(f => (
              <span key={f} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-400/20 text-yellow-400 border border-yellow-400/30">{f}</span>
            ))}
            {selectedFields.length > 5 && (
              <span className="px-2 py-0.5 rounded text-xs bg-gray-600 text-gray-300">+{selectedFields.length - 5} more</span>
            )}
            <button 
              onClick={onOpenFieldPicker} 
              className="absolute right-1 top-1 bottom-1 px-3 bg-gray-600 border border-gray-500 rounded text-xs font-medium text-yellow-400 hover:bg-gray-500 shadow-sm"
            >
              Edit
            </button>
          </div>
          <div className="flex justify-between items-center mt-2">
            <label className="flex items-center space-x-2 text-xs text-gray-400 cursor-pointer">
              <input 
                type="checkbox" 
                checked={useLabelHeaders} 
                onChange={(e) => setUseLabelHeaders(e.target.checked)} 
                className="rounded text-yellow-400 bg-gray-700 border-gray-600" 
              />
              <span>Friendly Headers</span>
            </label>
            <div className="flex items-center space-x-1">
              <span className="text-[10px] text-gray-500 uppercase font-bold">Batch:</span>
              <span className="text-xs text-gray-300">{useLegacyQuery ? '1000' : '2000'}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Legacy Query Mode Toggle - for accessing restriction fields */}
      <div className="mb-4 p-3 bg-gray-700/50 rounded border border-gray-600">
        <div className="flex items-center justify-between mb-2">
          <label className="flex items-center space-x-2 text-xs text-gray-300 cursor-pointer">
            <input 
              type="checkbox" 
              checked={useLegacyQuery} 
              onChange={(e) => {
                setUseLegacyQuery && setUseLegacyQuery(e.target.checked);
                if (e.target.checked && setUseGetMode) setUseGetMode(false);
              }}
              disabled={useGetMode}
              className="rounded text-orange-400 bg-gray-700 border-gray-600 disabled:opacity-50" 
            />
            <span className={`font-medium ${useGetMode ? 'text-gray-500' : ''}`}>Legacy Query Mode</span>
            <span className="text-[10px] text-orange-400 font-bold px-1.5 py-0.5 bg-orange-400/20 rounded">readByQuery</span>
          </label>
        </div>
        
        {useLegacyQuery && (
          <div className="mt-3 space-y-2">
            <div className="text-[10px] text-gray-400 leading-relaxed">
              <span className="text-orange-400 font-semibold">Use for restriction fields:</span> OBJECTRESTRICTION, RESTRICTEDLOCATIONS, RESTRICTEDDEPARTMENTS
              <br />
              <span className="text-gray-500">Simple query syntax (e.g., "STATUS = 'active'" or leave blank for all)</span>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Legacy Query Filter</label>
              <input 
                type="text" 
                value={legacyQueryString} 
                onChange={(e) => setLegacyQueryString && setLegacyQueryString(e.target.value)}
                placeholder="e.g., STATUS = 'active' (or leave blank for all)"
                className="w-full p-2 border border-gray-600 rounded text-xs bg-gray-700 text-white font-mono placeholder-gray-500"
              />
            </div>
            <div className="text-[10px] text-gray-500">
              <span className="font-semibold">✓</span> Auto-paginates all results using readMore
            </div>
          </div>
        )}
      </div>
      
      {/* GET Mode - Fetch complete records by ID */}
      <div className="mb-4 p-3 bg-gray-700/50 rounded border border-gray-600">
        <div className="flex items-center justify-between mb-2">
          <label className="flex items-center space-x-2 text-xs text-gray-300 cursor-pointer">
            <input 
              type="checkbox" 
              checked={useGetMode} 
              onChange={(e) => {
                setUseGetMode && setUseGetMode(e.target.checked);
                if (e.target.checked && setUseLegacyQuery) setUseLegacyQuery(false);
              }}
              disabled={useLegacyQuery}
              className="rounded text-cyan-400 bg-gray-700 border-gray-600 disabled:opacity-50" 
            />
            <span className={`font-medium ${useLegacyQuery ? 'text-gray-500' : ''}`}>GET Mode (Fetch by IDs)</span>
            <span className="text-[10px] text-cyan-400 font-bold px-1.5 py-0.5 bg-cyan-400/20 rounded">read/readByName</span>
          </label>
        </div>
        
        {useGetMode && (
          <div className="mt-3 space-y-3">
            <div className="text-[10px] text-gray-400 leading-relaxed">
              <span className="text-cyan-400 font-semibold">Fetch complete records by ID.</span> Returns ALL fields for each record.
            </div>
            
            {/* Key Type Selection */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-1.5 text-xs cursor-pointer">
                <input 
                  type="radio" 
                  name="getKeyType" 
                  value="name" 
                  checked={getKeyType === 'name'} 
                  onChange={() => setGetKeyType && setGetKeyType('name')}
                  className="text-cyan-400 bg-gray-700 border-gray-600"
                />
                <span className="text-gray-300">By Name</span>
                <span className="text-[10px] text-gray-500">(ITEMID, VENDORID, etc.)</span>
              </label>
              <label className="flex items-center space-x-1.5 text-xs cursor-pointer">
                <input 
                  type="radio" 
                  name="getKeyType" 
                  value="recordno" 
                  checked={getKeyType === 'recordno'} 
                  onChange={() => setGetKeyType && setGetKeyType('recordno')}
                  className="text-cyan-400 bg-gray-700 border-gray-600"
                />
                <span className="text-gray-300">By RECORDNO</span>
              </label>
            </div>
            
            {/* Excel Range Selection */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Keys Column</label>
              <div className="flex items-center gap-2">
                <button 
                  onClick={onLinkGetExcelRange}
                  className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-gray-300 rounded text-xs border border-gray-500 font-medium"
                >
                  Link Excel Column
                </button>
                {getExcelRef ? (
                  <span className="text-xs text-green-400 font-mono bg-green-400/10 px-2 py-1 rounded border border-green-400/30">{getExcelRef}</span>
                ) : (
                  <span className="text-xs text-gray-500 italic">No column selected</span>
                )}
              </div>
            </div>
            
            {/* Export XML Option */}
            <label className="flex items-center space-x-2 text-xs text-gray-400 cursor-pointer">
              <input 
                type="checkbox" 
                checked={exportGetXml} 
                onChange={(e) => setExportGetXml && setExportGetXml(e.target.checked)}
                className="rounded text-cyan-400 bg-gray-700 border-gray-600" 
              />
              <span>Export Response XML</span>
            </label>
            
            <div className="text-[10px] text-gray-500">
              <span className="font-semibold">✓</span> Select a column containing {getKeyType === 'recordno' ? 'RECORDNOs' : 'IDs'} • Batches in groups of 100
            </div>
          </div>
        )}
      </div>
      
      {/* Standard Filters - Only show when not in legacy mode or GET mode */}
      {!useLegacyQuery && !useGetMode && (
        <div className="mb-2">
          <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Filters</label>
          <div className="flex gap-2">
            <input 
              type="text" 
              readOnly 
              value={sqlFilter} 
              className="flex-1 p-2 border border-gray-600 rounded text-xs bg-gray-700 text-gray-300 font-mono truncate" 
            />
            <button 
              onClick={onOpenFilterBuilder} 
              className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-xs font-medium text-gray-300 hover:bg-gray-500 shadow-sm"
            >
              Edit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadModePanel;